// API Configuration for MaakDream Email Server
// This file handles all communication with the email server API

import { API_CONFIG, FEATURES, VALIDATION_RULES, RATE_LIMIT_CONFIG, FORM_TYPES, SUCCESS_MESSAGES, ERROR_MESSAGES } from '../config/environment';

// Get current environment configuration
const config = API_CONFIG;
const broker_url = process.env.REACT_APP_BROKER_URL;
// Common headers for all API requests
const getHeaders = (includeContentType = true) => {
    const headers = {
        'X-API-Key': config.apiKey
    };

    if (includeContentType) {
        headers['Content-Type'] = 'application/json';
    }

    return headers;
};

// Generic API request function
// const apiRequest = async (endpoint, data = null, method = 'GET', isFormData = false) => {
//     try {
//         const url = `${config.baseURL}${endpoint}`;
//         const options = {
//             method,
//             credentials: 'omit' // Don't send cookies for security
//         };

//         if (data && (method === 'POST' || method === 'PUT')) {
//             if (isFormData) {
//                 // For file uploads, don't set Content-Type header (browser will set it with boundary)
//                 // But we still need to set the API key header
//                 options.headers = getHeaders(false);
//                 options.body = data;
//             } else {
//                 // For JSON data
//                 options.headers = getHeaders(true);
//                 options.body = JSON.stringify(data);
//             }
//         } else {
//             options.headers = getHeaders(true);
//         }

//         // Debug logging
//         console.log('🔍 API Request Debug:');
//         console.log('📍 URL:', url);
//         console.log('🔑 API Key:', config.apiKey);
//         console.log('📋 Headers:', options.headers);
//         console.log('📦 Method:', method);
//         console.log('📄 Is FormData:', isFormData);

//         const response = await fetch(url, options);

//         // Debug response
//         console.log('📡 Response Status:', response.status, response.statusText);
//         console.log('📋 Response Headers:', Object.fromEntries(response.headers.entries()));

//         // Handle non-2xx responses
//         if (!response.ok) {
//             const errorData = await response.json().catch(() => ({}));
//             console.log('❌ Error Response Data:', errorData);
//             throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
//         }

//         return await response.json();
//     } catch (error) {
//         console.error('API Request Error:', error);
//         throw error;
//     }
// };

const apiRequest = async (endpoint, data = null, method = 'GET', isFormData = false, baseUrl = null) => {
    try {
        // Use the passed baseUrl or fall back to the default baseURL in the config
        const url = `${baseUrl || config.baseURL}${endpoint}`;
        const options = {
            method,
            credentials: 'omit' // Don't send cookies for security
        };

        if (data && (method === 'POST' || method === 'PUT')) {
            if (isFormData) {
                // For file uploads, don't set Content-Type header (browser will set it with boundary)
                // But we still need to set the API key header
                options.headers = getHeaders(false);
                options.body = data;
            } else {
                // For JSON data
                options.headers = getHeaders(true);
                options.body = JSON.stringify(data);
            }
        } else {
            options.headers = getHeaders(true);
        }

        // Debug logging
        console.log('🔍 API Request Debug:');
        console.log('📍 URL:', url);
        console.log('🔑 API Key:', config.apiKey);
        console.log('📋 Headers:', options.headers);
        console.log('📦 Method:', method);
        console.log('📄 Is FormData:', isFormData);

        const response = await fetch(url, options);

        // Debug response
        console.log('📡 Response Status:', response.status, response.statusText);
        console.log('📋 Response Headers:', Object.fromEntries(response.headers.entries()));

        // Handle non-2xx responses
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.log('❌ Error Response Data:', errorData);
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        // Parse the response
        const jsonData = await response.json();

        // ✅ NORMALIZE THE RESPONSE - Add success flag for 2xx status codes
        return {
            success: true,
            status: response.status,
            data: jsonData,
            ...jsonData // Spread the original response data
        };

    } catch (error) {
        console.error('API Request Error:', error);
        // Return normalized error format
        return {
            success: false,
            message: error.message || 'An error occurred',
            error: error
        };
    }
};


// Health check endpoint
export const checkEmailServerHealth = async () => {
    return await apiRequest('/health');
};

// Meeting request form submission
export const sendMeetingRequest = async (formData) => {
    const data = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: formData.date,
        time: formData.time,
        propertyInterest: formData.propertyInterest,
        message: formData.message
    };

    return await apiRequest('/api/send-meeting-request', data, 'POST');
};

// Broker registration form submission with file handling
export const sendBrokerRegistration = async (formData) => {
    const formDataObj = new FormData();
    console.log(formData?.companyName, formData?.email)
    // ---- TEXT FIELDS (Add ONLY valid fields required by backend) ---- //
    const fields = {
        company_name: formData.companyName,
        email: formData.email,
        relationship_manager: formData.relationshipManager,
        office_phone_number: formData.officeNumber,
        website: formData.website,
        company_whatsapp: formData.companyWhatsapp,
        general_manager_whatsapp: formData.gmWhatsapp,
        accounts_contact_number: formData.accountsContact,
        country: formData.country,
        complete_address: formData.address,
        city: formData.city,
        state_province: formData.state,
        postal_code: formData.zipCode
    };

    Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            formDataObj.append(key, value);
        }
    });

    // ---- FILE FIELDS ---- //
    if (formData.tradeLicense)
        formDataObj.append("trade_license", formData.tradeLicense);

    if (formData.reraCertificate)
        formDataObj.append("rera_certificate", formData.reraCertificate);

    if (formData.taxRegistrationCertificate)
        formDataObj.append("tax_registration_certificate", formData.taxRegistrationCertificate);

    if (formData.passportCopy)
        formDataObj.append("passport_copy_owner_gm", formData.passportCopy);

    if (formData.otherDocuments)
        formDataObj.append("other_supporting_documents", formData.otherDocuments);

    // ---- API CALL (Always FormData) ---- //
    return await apiRequest(
        "/agency-broker/",
        formDataObj,
        "POST",
        true, // isFormData
        broker_url
    );
};

export const sendIndividualBrokerRegistration = async (formData) => {
    const formDataObj = new FormData();

    // ---- TEXT FIELDS (Matches backend schema exactly) ----
    const fields = {
        full_name: formData.full_name,
        email: formData.email,
        phone_number: formData.phone_number,
        whatsapp_number: formData.whatsapp_number,
        date_of_birth: formData.date_of_birth,
        nationality: formData.nationality,
        gender: formData.gender,
        years_of_experience: formData.years_of_experience,
        specialization: formData.specialization,
        languages_spoken: formData.languages_spoken,
        current_company: formData.current_company,
        rera_number: formData.rera_number
    };

    Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            formDataObj.append(key, value);
        }
    });

    // ---- FILE FIELDS ----
    if (formData.passport_copy)
        formDataObj.append("passport_copy", formData.passport_copy);

    // if (formData.resume_cv)
    //     formDataObj.append("resume_cv", formData.resume_cv);

    if (formData.rera_license)
        formDataObj.append("rera_license", formData.rera_license);

    // supports single file or array
    if (formData.other_supporting_documents) {
        if (Array.isArray(formData.other_supporting_documents)) {
            formData.other_supporting_documents.forEach(file => {
                formDataObj.append("other_supporting_documents", file);
            });
        } else {
            formDataObj.append("other_supporting_documents", formData.other_supporting_documents);
        }
    }

    // ---- API CALL ----
    return await apiRequest(
        "/individual-brokers/",
        formDataObj,
        "POST",
        true,
        broker_url
    );
};


// Contact form submission
export const sendContactForm = async (formData) => {
    const data = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject || formData.propertyType || 'General Inquiry',
        message: formData.message
    };

    return await apiRequest('/api/send-contact-form', data, 'POST');
};

// Form validation utilities
export const validateEmail = (email) => {
    return VALIDATION_RULES.EMAIL.pattern.test(email);
};

export const validatePhone = (phone) => {
    // Remove all non-digit characters for validation
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= VALIDATION_RULES.PHONE.minLength;
};

export const validateRequired = (value) => {
    return value && value.trim().length >= VALIDATION_RULES.NAME.minLength;
};

// Rate limiting helper (client-side)
class RateLimiter {
    constructor() {
        this.requests = new Map();
        this.limits = {
            perMinute: RATE_LIMIT_CONFIG.PER_MINUTE,
            perHour: RATE_LIMIT_CONFIG.PER_HOUR
        };
    }

    canMakeRequest(endpoint) {
        const now = Date.now();
        const key = `${endpoint}_${Math.floor(now / 60000)}`; // Per minute
        const hourKey = `${endpoint}_${Math.floor(now / 3600000)}`; // Per hour

        // Check minute limit
        const minuteCount = this.requests.get(key) || 0;
        if (minuteCount >= this.limits.perMinute) {
            return false;
        }

        // Check hour limit
        const hourCount = this.requests.get(hourKey) || 0;
        if (hourCount >= this.limits.perHour) {
            return false;
        }

        // Update counters
        this.requests.set(key, minuteCount + 1);
        this.requests.set(hourKey, hourCount + 1);

        // Clean up old entries
        setTimeout(() => {
            this.requests.delete(key);
        }, 60000);

        return true;
    }
}

export const rateLimiter = new RateLimiter();

// Error handling utilities
export const handleApiError = (error, formType = 'form') => {

    if (error.message.includes('rate limit')) {
        return ERROR_MESSAGES.RATE_LIMIT_ERROR;
    }

    if (error.message.includes('network')) {
        return ERROR_MESSAGES.NETWORK_ERROR;
    }

    if (error.message.includes('401') || error.message.includes('403')) {
        return ERROR_MESSAGES.AUTH_ERROR;
    }

    return ERROR_MESSAGES.UNKNOWN_ERROR;
};

// Success message utilities
export const getSuccessMessage = (formType) => {
    return SUCCESS_MESSAGES[formType] || 'Form submitted successfully!';
};

export default {
    checkEmailServerHealth,
    sendMeetingRequest,
    sendBrokerRegistration,
    sendContactForm,
    validateEmail,
    validatePhone,
    validateRequired,
    rateLimiter,
    handleApiError,
    getSuccessMessage
}; 